/* global Word */

// ═══════════════════════════════════════════════════════════════════════════
// RUN 5: Extra Spaces Checker (Two-pass) + Range-Level Go To
// ═══════════════════════════════════════════════════════════════════════════
//
// Pass 1: Collapse multiple consecutive spaces — BODY ONLY, and preserve
//         leading indentation (a paragraph that intentionally starts with
//         two or more spaces is left alone).
// Pass 2: Insert a missing space after a sentence-ending period when the
//         next character is an uppercase letter — BODY ONLY.
//
// Both passes skip text inside table cells so aligned tabular content is
// not disturbed.
// ═══════════════════════════════════════════════════════════════════════════


import {
  safeAlert, reportResult, rangeDetail,
  RunDetail, SRS_NAV_PREFIX, MAX_NAV_ITEMS
} from '../runs/common';
import { getConfig } from '../taskpane/configService';

/** Bookmark prefix for this run */
const BM_PREFIX = SRS_NAV_PREFIX + "R5_";


// ─────────────────────────────────────────────────────────────────────────
// Shared types
// ─────────────────────────────────────────────────────────────────────────

interface NavState {
  navDetails: RunDetail[];
  navCounter: { count: number };
}

interface Pass1Result {
  totalRemoved:       number;
  passesRun:          number;
  totalSkippedTable:  number;
  totalSkippedLeading: number;
}

interface Pass2Result {
  added:        number;
  skippedTable: number;
}


// ─────────────────────────────────────────────────────────────────────────
// Shared bookmark helper
// ─────────────────────────────────────────────────────────────────────────

function insertNavBookmark(
  range: Word.Range,
  text:  string,
  note:  string,
  nav:   NavState
): void {
  if (nav.navCounter.count >= MAX_NAV_ITEMS) return;
  const bmName = BM_PREFIX + String(nav.navCounter.count).padStart(3, "0");
  range.insertBookmark(bmName);
  nav.navDetails.push(rangeDetail(bmName, text, note));
  nav.navCounter.count++;
}


// ═══════════════════════════════════════════════════════════════════════════
// PASS 1 helpers  (extracted to reduce S3776 complexity 18 → ≤ 15)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * For each double-space match, builds the "pre" range from the paragraph
 * start to the match start so we can detect leading-indent matches.
 * Also batch-loads the parent-table state.
 */
async function loadPass1RangeMetadata(
  matches: Word.RangeCollection,
  context: Word.RequestContext
): Promise<Word.Range[]> {
  const preRanges: Word.Range[] = [];
  for (const m of matches.items) {
    const paraStart  = m.paragraphs.getFirst().getRange("Start");
    const matchStart = m.getRange("Start");
    const pre        = paraStart.expandTo(matchStart);
    pre.load("text");
    preRanges.push(pre);
    m.parentTableOrNullObject.load("isNullObject");
  }
  await context.sync();
  return preRanges;
}

/** Returns pass-level counts after applying (or skipping) one set of matches. */
async function applyPass1Matches(
  matches:   Word.RangeCollection,
  preRanges: Word.Range[],
  passNum:   number,
  nav:       NavState
): Promise<{ removed: number; skippedTable: number; skippedLeading: number }> {
  let removed        = 0;
  let skippedTable   = 0;
  let skippedLeading = 0;

  for (let i = 0; i < matches.items.length; i++) {
    const range = matches.items[i];

    if (!range.parentTableOrNullObject.isNullObject) { skippedTable++;   continue; }
    if (/^\s*$/.test(preRanges[i].text || ""))       { skippedLeading++; continue; }

    range.insertText(" ", Word.InsertLocation.replace);
    insertNavBookmark(range, "Double space removed", "pass " + passNum, nav);
    removed++;
  }
  return { removed, skippedTable, skippedLeading };
}

// ─────────────────────────────────────────────────────────────────────────
// Pass 1: Remove multiple consecutive spaces
//   • Skip matches whose parent is a table cell.
//   • Skip matches whose preceding paragraph content is entirely whitespace
//     (i.e., the match lies inside the paragraph's leading indentation).
// ─────────────────────────────────────────────────────────────────────────

async function removeDoubleSpaces(
  context:   Word.RequestContext,
  maxPasses: number,
  nav:       NavState
): Promise<Pass1Result> {
  let totalRemoved        = 0;
  let passesRun           = 0;
  let totalSkippedTable   = 0;
  let totalSkippedLeading = 0;

  console.log("Pass 1: Removing multiple consecutive spaces (body only, preserving paragraph-start indent)");

  for (let pass = 1; pass <= maxPasses; pass++) {
    const foundRanges = context.document.body.search("  ", {
      matchCase: false,
      matchWholeWord: false,
    });
    foundRanges.load("items");
    await context.sync();

    if (foundRanges.items.length === 0) {
      console.log(`  Pass ${pass}: 0 double-space(s) found`);
      break;
    }

    const preRanges = await loadPass1RangeMetadata(foundRanges, context);
    const { removed, skippedTable, skippedLeading } =
      await applyPass1Matches(foundRanges, preRanges, pass, nav);
    await context.sync();

    console.log(
      `  Pass ${pass}: ${foundRanges.items.length} found | ${removed} removed | ` +
      `${skippedTable} skipped (table) | ${skippedLeading} skipped (leading indent)`
    );

    totalRemoved        += removed;
    totalSkippedTable   += skippedTable;
    totalSkippedLeading += skippedLeading;
    passesRun = pass;

    if (removed === 0) break;
  }

  console.log("");
  return { totalRemoved, passesRun, totalSkippedTable, totalSkippedLeading };
}


// ═══════════════════════════════════════════════════════════════════════════
// PASS 2 helpers  (extracted to reduce S3776 complexity 19 → ≤ 15)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Validates that a wildcard match is exactly the 3-char pattern we expect:
 * lowercase + "." + uppercase.
 * Returns the three characters, or null if the match is malformed.
 */
function parsePeriodicMatch(text: string): { ch0: string; ch2: string } | null {
  if (text.length !== 3)         return null;
  const ch0 = text.charAt(0);
  const ch1 = text.charAt(1);
  const ch2 = text.charAt(2);
  if (ch1 !== ".")               return null;
  if (!(ch0 >= "a" && ch0 <= "z")) return null;
  if (!(ch2 >= "A" && ch2 <= "Z")) return null;
  return { ch0, ch2 };
}

/** Batch-loads text + parent-table state for all period matches. */
async function loadPass2RangeMetadata(
  matches: Word.RangeCollection,
  context: Word.RequestContext
): Promise<void> {
  // S4138: for-of instead of for(let i...)
  for (const range of matches.items) {
    range.load("text");
    range.parentTableOrNullObject.load("isNullObject");
  }
  await context.sync();
}

/** Applies (or skips) all validated period matches. */
async function applyPass2Matches(
  matches: Word.RangeCollection,
  nav:     NavState
): Promise<Pass2Result> {
  let added        = 0;
  let skippedTable = 0;

  // S4138: for-of instead of for(let i...)
  for (const range of matches.items) {
    if (!range.parentTableOrNullObject.isNullObject) { skippedTable++; continue; }

    const parsed = parsePeriodicMatch(range.text || "");
    if (parsed === null) continue;

    const replacement = parsed.ch0 + ". " + parsed.ch2;
    range.insertText(replacement, Word.InsertLocation.replace);
    insertNavBookmark(range, `${range.text} \u2192 ${replacement}`, "missing space added", nav);
    added++;
  }
  return { added, skippedTable };
}

// ─────────────────────────────────────────────────────────────────────────
// Pass 2: Add missing space after period before capital letter (body only)
// ─────────────────────────────────────────────────────────────────────────

async function addMissingSpacesAfterPeriod(
  context: Word.RequestContext,
  nav:     NavState
): Promise<Pass2Result> {
  console.log("Pass 2: Adding missing space after period before capital letter (body only)");

  // Why this wildcard pattern instead of 26 literal `.A`..`.Z` searches:
  //
  // Requiring a LOWERCASE LETTER immediately before the dot lets us catch
  // sentence boundaries (`end.Next`) while leaving section IDs (`3.2.P.4`),
  // decimals, and dotted abbreviations (`e.g.`, `i.e.`) alone — none of
  // those has a lowercase letter immediately before the relevant dot.
  // matchCase is required because `[a-z]` / `[A-Z]` only respect case
  // under case-sensitive matching. In wildcard mode `.` is a LITERAL
  // period (NOT "any char").

  const foundRanges = context.document.body.search("[a-z].[A-Z]", {
    matchCase:      true,
    matchWholeWord: false,
    matchWildcards: true,
  });
  foundRanges.load("items");
  await context.sync();

  if (foundRanges.items.length === 0) {
    console.log(`  \u2713 No missing spaces after periods found\n`);
    return { added: 0, skippedTable: 0 };
  }

  await loadPass2RangeMetadata(foundRanges, context);
  const result = await applyPass2Matches(foundRanges, nav);

  if (result.added > 0) {
    await context.sync();
    console.log(`  \u2713 Added ${result.added} space(s) after period(s) | ${result.skippedTable} skipped (table)\n`);
  } else {
    console.log(`  \u2713 No missing spaces added | ${result.skippedTable} skipped (table)\n`);
  }

  return result;
}


// ─────────────────────────────────────────────────────────────────────────
// Entry point
// ─────────────────────────────────────────────────────────────────────────

async function runImpl(context: Word.RequestContext): Promise<void> {
  const cfg  = getConfig();
  const sCfg = cfg.run5_spaces;

  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

  console.log("=".repeat(70));
  console.log(`=== EXTRA SPACES CHECKER (${cfg.clientName}) ===`);
  console.log("=".repeat(70));

  const nav: NavState = { navDetails: [], navCounter: { count: 0 } };

  const { totalRemoved, passesRun, totalSkippedTable, totalSkippedLeading } =
    await removeDoubleSpaces(context, sCfg.maxPasses, nav);
  const { added: missingSpacesAdded, skippedTable: pass2SkippedTable } =
    await addMissingSpacesAfterPeriod(context, nav);

  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();

  const totalCorrections = totalRemoved + missingSpacesAdded;

  console.log("=".repeat(70));
  console.log(`Passes run:                    ${passesRun}`);
  console.log(`Extra spaces removed:          ${totalRemoved}`);
  console.log(`  skipped (table):             ${totalSkippedTable}`);
  console.log(`  skipped (leading indent):    ${totalSkippedLeading}`);
  console.log(`Missing spaces added:          ${missingSpacesAdded}`);
  console.log(`  skipped (table):             ${pass2SkippedTable}`);
  console.log(`Total corrections:             ${totalCorrections}`);
  console.log(`Go To bookmarks placed:        ${nav.navCounter.count}`);
  console.log("=".repeat(70));

  if (totalCorrections > 0) {
    reportResult('Extra Spaces Fixed', [
      { label: 'Extra spaces removed',           count: totalRemoved },
      { label: '  (skipped inside tables)',      count: totalSkippedTable },
      { label: '  (preserved leading indents)',  count: totalSkippedLeading },
      { label: 'Missing spaces added',           count: missingSpacesAdded },
      { label: '  (skipped inside tables)',      count: pass2SkippedTable },
      { label: 'Total corrections',              count: totalCorrections },
      ...nav.navDetails,
    ]);
  } else {
    reportResult('Extra Spaces \u2014 no changes needed', [
      { label: 'No body-level spacing issues found', note: 'No changes made' },
      { label: 'Skipped inside tables',        count: totalSkippedTable + pass2SkippedTable },
      { label: 'Preserved leading indents',    count: totalSkippedLeading },
    ]);
  }
}

export async function run5() {
  await Word.run(async (context) => {
    try {
      await runImpl(context);
    } catch (error) {
      console.error("Error in run5:", error);
      safeAlert("An error occurred during the extra spaces check.");
    }
  });
}